// src/components/shared/PnlChart.jsx

import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  Area,
  ReferenceLine
} from 'recharts';

const formatAxisValue = (value) => {
  const num = Number(value);
  if (!Number.isFinite(num)) return '';
  const abs = Math.abs(num);
  const formatted = abs >= 1000
    ? abs.toLocaleString('en-US', { maximumFractionDigits: 0 })
    : abs.toLocaleString('en-US', { maximumFractionDigits: 2 });
  return `${num < 0 ? '-' : ''}$${formatted}`;
};

const formatTooltipValue = (value) => {
  const num = Number(value);
  if (!Number.isFinite(num)) return '-';
  return `${num < 0 ? '-' : ''}$${Math.abs(num).toLocaleString('en-US', { maximumFractionDigits: 4 })}`;
};

const getTimeFormatter = (spanMs) => (value) => {
  if (!value) return '';
  const date = new Date(value);
  if (!Number.isFinite(date.getTime())) return '';
  if (spanMs && spanMs <= 36 * 60 * 60 * 1000) {
    return date.toLocaleTimeString(undefined, { hour: '2-digit', minute: '2-digit' });
  }
  return date.toLocaleDateString(undefined, { month: 'short', day: '2-digit' });
};

const getTooltipTime = (value, spanMs) => {
  if (!value) return '';
  const date = new Date(value);
  if (!Number.isFinite(date.getTime())) return '';
  if (spanMs && spanMs <= 36 * 60 * 60 * 1000) {
    return date.toLocaleString(undefined, { month: 'short', day: '2-digit', hour: '2-digit', minute: '2-digit' });
  }
  return date.toLocaleDateString(undefined, { month: 'short', day: '2-digit', year: 'numeric' });
};

const PnlTooltip = ({ active, payload, label, spanMs }) => {
  if (!active || !payload || payload.length === 0) return null;
  const value = payload[0]?.value;
  return (
    <div className="rounded-xl border border-slate-700/80 bg-slate-900/90 px-4 py-3 text-xs text-slate-200 shadow-2xl">
      <div className="text-[10px] uppercase tracking-widest text-slate-400">PNL</div>
      <div className="mt-1 text-sm font-semibold text-white">{formatTooltipValue(value)}</div>
      <div className="mt-2 text-[11px] text-slate-400">{getTooltipTime(label, spanMs)}</div>
    </div>
  );
};

const PnlChart = ({ data = [], dataKey = 'pnl', xKey = 'time' }) => {
  const spanMs = data.length > 1
    ? Number(data[data.length - 1]?.[xKey]) - Number(data[0]?.[xKey])
    : 0;
  const timeFormatter = getTimeFormatter(spanMs);

  return (
    <ResponsiveContainer width="100%" height="100%">
      <LineChart data={data} margin={{ top: 10, right: 24, left: 6, bottom: 10 }}>
        <defs>
          <linearGradient id="pnlStroke" x1="0" y1="0" x2="1" y2="0">
            <stop offset="0%" stopColor="#38bdf8" />
            <stop offset="100%" stopColor="#22d3ee" />
          </linearGradient>
          <linearGradient id="pnlFill" x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%" stopColor="#38bdf8" stopOpacity={0.25} />
            <stop offset="70%" stopColor="#0f172a" stopOpacity={0.08} />
            <stop offset="100%" stopColor="#0b1120" stopOpacity={0} />
          </linearGradient>
        </defs>
        <CartesianGrid strokeDasharray="4 6" stroke="#1f2937" vertical={false} />
        <XAxis
          dataKey={xKey}
          type="number"
          domain={['dataMin', 'dataMax']}
          tickFormatter={timeFormatter}
          tick={{ fontSize: 11, fill: '#94a3b8' }}
          tickMargin={12}
          tickLine={false}
          axisLine={false}
          minTickGap={28}
        />
        <YAxis
          tickFormatter={formatAxisValue}
          tick={{ fontSize: 11, fill: '#94a3b8' }}
          tickMargin={8}
          width={70}
          axisLine={false}
          tickLine={false}
        />
        <ReferenceLine y={0} stroke="#334155" strokeDasharray="6 6" />
        <Tooltip content={<PnlTooltip spanMs={spanMs} />} cursor={{ stroke: '#1f2937', strokeDasharray: '4 6' }} />
        <Area
          type="monotone"
          dataKey={dataKey}
          stroke="none"
          fill="url(#pnlFill)"
          isAnimationActive={false}
        />
        <Line
          type="monotone"
          dataKey={dataKey}
          stroke="url(#pnlStroke)"
          strokeWidth={2.4}
          dot={false}
          activeDot={{ r: 4, fill: '#38bdf8', stroke: '#0b1120', strokeWidth: 2 }}
        />
      </LineChart>
    </ResponsiveContainer>
  );
};

export default PnlChart;
